import { DatePicker, Input, InputNumber, Select, SelectProps } from 'antd';
import moment from 'moment';
import { constants, DetailKey } from 'procura-shared';
import React, { useEffect, useMemo, useState } from 'react';

type DetailType = 'string' | 'number' | 'timestamp' | 'select';
type DetailValue = string | number;

type DetailInputProps = {
  value: DetailValue;
  detailKey?: DetailKey;
  placeholder?: string;
  onChange?: (value: DetailValue) => void;
  options?: SelectProps<string>['options'];
};

export const DetailInput = ({
  value: defaultValue,
  detailKey = null,
  placeholder,
  onChange,
  options,
}: DetailInputProps) => {
  const [value, setValue] = useState<DetailValue>(defaultValue);

  const isDate = useMemo((): boolean => constants.DATE_KEYS.includes(detailKey), [detailKey]);
  const isCurrency = useMemo((): boolean => constants.CURRENCY_KEYS.includes(detailKey), [detailKey]);

  const type = useMemo((): DetailType => {
    if (isDate) return 'timestamp';
    if (isCurrency) return 'number';
    if (options?.length) return 'select';
    return (typeof defaultValue).toString() as DetailType;
  }, [defaultValue, isCurrency, isDate, options?.length]);

  const valueAsMomentjs = useMemo(
    (): moment.Moment => (!!value && moment(value).isValid() ? moment(value) : undefined),
    [value]
  );
  const valueAsNumber = useMemo(() => (Number.isNaN(value) ? 0 : Number(value)), [value]);

  const stringChangeHandler = (e: any) => updateValue(e.target.value);
  const numberChangeHandler = (e: number) => updateValue(e);
  const dateChangeHandler = (e: moment.Moment) => updateValue(Number(e));
  const selectChangeHandler = (value: string) => updateValue(value);

  useEffect(() => {
    setValue(defaultValue);
  }, [detailKey, defaultValue]);

  const updateValue = (newValue: DetailValue) => {
    setValue(newValue);
    onChange?.(newValue);
  };

  const renderInput = () => {
    switch (type) {
      default:
      case 'string':
        return (
          <Input style={{ minWidth: 200 }} onChange={stringChangeHandler} value={value} placeholder={placeholder} />
        );
      case 'number':
        return (
          <InputNumber
            style={{ minWidth: 200 }}
            onChange={numberChangeHandler}
            value={valueAsNumber}
            placeholder={placeholder}
          />
        );
      case 'timestamp':
        return (
          <DatePicker
            style={{ minWidth: 200 }}
            onChange={dateChangeHandler}
            value={valueAsMomentjs}
            placeholder={placeholder}
          />
        );
      case 'select':
        return (
          <Select<string>
            value={value as string}
            onChange={selectChangeHandler}
            placeholder={placeholder}
            options={options ?? []}
            style={{
              minWidth: 200,
            }}
          />
        );
    }
  };

  return renderInput();
};
